// src/contexts/CombinationContext.js
import React, { createContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { fetchWithRetry } from '../utils/fetchWithRetry';

export const CombinationContext = createContext();

export const CombinationProvider = ({ children }) => {
  const [kombinationen, setKombinationen] = useState([]);
  const [loadingKombinationen, setLoadingKombinationen] = useState(true);
  const [errorKombinationen, setErrorKombinationen] = useState(null);

  const [kombinationDetailCache, setKombinationDetailCache] = useState({});
  const [classKombinationCache, setClassKombinationCache] = useState({});
  const inFlightRequestsRef = useRef({});

  const KOMBI_API_URL =
    'https://admin.thirsty-shtern.91-151-16-181.plesk.page/items/Kombinationen?fields=*.*.*&filter[status][_eq]=published';

  useEffect(() => {
    const ac = new AbortController();
    const fetchKombinationen = async () => {
      setLoadingKombinationen(true); setErrorKombinationen(null);
      try {
        const res = await fetchWithRetry(
          () => axios.get(KOMBI_API_URL, { signal: ac.signal }),
          { retries: 3, baseDelay: 300, signal: ac.signal }
        );
        setKombinationen(res?.data?.data ?? []);
      } catch (e) {
        if (!ac.signal.aborted) setErrorKombinationen('Fehler beim Laden der Kombinationen');
      } finally {
        if (!ac.signal.aborted) setLoadingKombinationen(false);
      }
    };
    fetchKombinationen();
    /* return () => ac.abort(); */
  }, []);

  const fetchCombinationDetails = async (id) => {
    if (kombinationDetailCache[id]) return kombinationDetailCache[id];
    if (inFlightRequestsRef.current[id]) return inFlightRequestsRef.current[id];

    const promise = (async () => {
      try {
        const kombiDetailUrl =
          `https://admin.thirsty-shtern.91-151-16-181.plesk.page/items/Kombinationen/${id}?fields=*.*.*`;
        const ausbildungenUrl =
          `https://admin.thirsty-shtern.91-151-16-181.plesk.page/items/Ausbildungen?filter[Kombi][_eq]=${id}&fields=*,Blocks.*,Blocks.item.*,Fragen.*,Fragen.Fragen_id.*`;

        const [kombiResp, ausbResp] = await Promise.all([
          fetchWithRetry(() => axios.get(kombiDetailUrl), { retries: 3, baseDelay: 300 }),
          fetchWithRetry(() => axios.get(ausbildungenUrl), { retries: 3, baseDelay: 300 }),
        ]);

        const combinationData = kombiResp?.data?.data ?? null;
        const ausbildungen = ausbResp?.data?.data ?? [];

        let allBlocks = [];
        let allFragen = [];
        ausbildungen.forEach((a) => {
          if (a.Blocks) allBlocks = [...allBlocks, ...a.Blocks];
          if (a.Fragen) allFragen = [...allFragen, ...a.Fragen];
        });

        const detail = { combinationData, ausbildungen, blocks: allBlocks, fragen: allFragen };
        setKombinationDetailCache((prev) => ({ ...prev, [id]: detail }));
        return detail;
      } finally {
        delete inFlightRequestsRef.current[id];
      }
    })();

    inFlightRequestsRef.current[id] = promise;
    return promise;
  };

  // const fetchKombinationsForClass = async (classId) => {
  //   if (classKombinationCache[classId]) {
  //     const ids = classKombinationCache[classId];
  //     return ids.map(id => kombinationen.find(k => k.id === id)).filter(Boolean);
  //   }
  //   try {
  //     const url = `https://admin.thirsty-shtern.91-151-16-181.plesk.page/items/Klassen_Kombinationen?filter[Klassen_id][_eq]=${classId}&fields=Kombinationen_id.id,Kombinationen_id.Name`;
  //     const response = await fetchWithRetry(() => axios.get(url), { retries: 3, baseDelay: 300 });
  //     const kombis = (response?.data?.data ?? []).map(item => item.Kombinationen_id).filter(Boolean);
  //     const kombiIds = kombis.map(k => k.id);
  //     setClassKombinationCache(prev => ({ ...prev, [classId]: kombiIds }));
  //     return kombis;
  //   } catch (e) {
  //     console.error("Error fetching Kombinations for Class:", e);
  //     return [];
  //   }
  // };

  const fetchKombinationsForClass = async (classId) => {
  if (classKombinationCache[classId]) {
    const ids = classKombinationCache[classId];
    return ids.map(id => kombinationen.find(k => k.id === id)).filter(Boolean);
  }

  try {
    const url = `https://admin.thirsty-shtern.91-151-16-181.plesk.page/items/Klassen_Kombinationen`;

    const response = await fetchWithRetry(
      () => axios.get(url, {
        params: {
          "filter[Klassen_id][_eq]": classId,
          // забираем сорт и саму kombi
          fields: "sort_kombis,Kombinationen_id.id,Kombinationen_id.Name",
          // сортируем на сервере
          sort: "sort_kombis",
          limit: 200
        }
      }),
      { retries: 3, baseDelay: 300 }
    );

    const rows = response?.data?.data ?? [];

    // на всякий случай можно продублировать сортировку на клиенте
    rows.sort((a, b) => (a.sort_kombis ?? 0) - (b.sort_kombis ?? 0));

    const kombis = rows.map(r => r.Kombinationen_id).filter(Boolean);

    // важно: в кеш кладём ids уже в правильном порядке
    const kombiIds = kombis.map(k => k.id);
    setClassKombinationCache(prev => ({ ...prev, [classId]: kombiIds }));

    return kombis;
  } catch (e) {
    console.error("Error fetching Kombinations for Class:", e);
    return [];
  }
};


  return (
    <CombinationContext.Provider value={{
      kombinationen,
      loadingKombinationen,
      errorKombinationen,
      fetchCombinationDetails,
      fetchKombinationsForClass,
    }}>
      {children}
    </CombinationContext.Provider>
  );
};
